import { useMutation } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import { graphql } from '~/apollo/generated/v4';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { useZodForm } from '~/utils/forms';
import { VftFormFields } from './vft-form-fields';
import { initialVft, vftSchema } from '~/utils/modules/vft';
import { ADD_OUTCROP_TO_VFT } from './create-vft-form';
import type { ReactNode } from 'react';
import { useModalState } from '~/hooks/modal';
import { Modal } from '~/components/common/Modal';
import type { VftManagerVftType } from '~/routes/upload/vft-manager/vft-manager';

const UPDATE_VFT = graphql(`
  mutation UpdateVft($id: ID!, $input: VirtualFieldTripUpdateInput!) {
    virtualFieldTripUpdate(id: $id, input: $input) {
      result {
        id
        lifecycleStatus
        name
        outcropTour
      }
    }
  }
`);

const REMOVE_OUTCROP_FROM_VFT = graphql(`
  mutation RemoveOutcropFromVft($input: VirtualFieldTripRemoveOutcropInput!) {
    virtualFieldTripRemoveOutcrop(input: $input)
  }
`);

export function UpdateVftModal({
  vft,
  children,
}: {
  vft: VftManagerVftType;
  children: (showModal: () => void) => ReactNode;
}) {
  const { show, showModal, hideModal } = useModalState();
  const [updateVft, { loading, error }] = useMutation(UPDATE_VFT);
  const [addOutcrop, { loading: addOutcropLoading, error: addOutcropError }] =
    useMutation(ADD_OUTCROP_TO_VFT);
  const [
    removeOutcrop,
    { loading: removeOutcropLoading, error: removeOutcropError },
  ] = useMutation(REMOVE_OUTCROP_FROM_VFT);

  const form = useZodForm({
    schema: vftSchema,
    values: initialVft(vft),
  });

  const handleSubmit = form.handleSubmit(
    async (values: z.infer<typeof vftSchema>) => {
      const { ...input } = values;
      if (!vft) {
        return;
      }
      const newOutcrops = input.outcrops || [];
      const prevOutcrops = vft.outcrops?.map(oc => oc.id) || [];
      delete input.outcrops;
      try {
        const res = await updateVft({
          variables: { id: vft?.id, input },
        });
        const vftId = res.data?.virtualFieldTripUpdate?.result?.id;
        if (!vftId) {
          toast.error('There was a problem saving the vft.');
          return;
        }
        for (const outcropId of prevOutcrops) {
          try {
            await removeOutcrop({
              variables: {
                input: {
                  virtualFieldTripId: Number(vftId),
                  outcropId: Number(outcropId),
                },
              },
            });
          } catch (err) {
            console.log('Error removing vft outcrop', err);
            toast.error(`There was a problem removing outcrop ${outcropId}.`);
          }
        }

        for (const outcropId of newOutcrops) {
          try {
            await addOutcrop({
              variables: {
                input: {
                  virtualFieldTripId: Number(vftId),
                  outcropId: outcropId,
                },
              },
            });
          } catch (err) {
            console.log('Error saving vft outcrop', err);
            toast.error(`There was a problem adding outcrop ${outcropId}.`);
          }
        }
        toast.success('VFT saved successfully.');
        hideModal();
      } catch (err) {
        console.log('Error updating vft', err);
        toast.error('There was a problem saving the vft.');
      }
    },
  );

  return (
    <>
      {children(showModal)}
      <Modal open={show} onHide={hideModal}>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <VftFormFields vft={vft}/>
            <HookFormErrors
              graphQLError={error || addOutcropError || removeOutcropError}
            />

            <div className="text-center m-2 space-x-2">
              <Button
                type="submit"
                color="primary"
                disabled={loading || addOutcropLoading || removeOutcropLoading}
              >
                Save
              </Button>
              <Button
                type="button"
                color="neutral"
                disabled={loading || addOutcropLoading || removeOutcropLoading}
                onClick={hideModal}
              >
                Cancel
              </Button>
            </div>
          </form>
        </FormProvider>
      </Modal>
    </>
  );
}
